import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["album", "showCheckboxes", "hideCheckboxes", "star", "viewAlbum", "addButton", "count", "iconsContainer", "snipButton", "snipIcon", "snipLabel"]
  static values = { persistKey: { type: String, default: "selectedAlbumId" }, albumName: { type: String, default: "album" } }

  initialize() {
    // Initialize controller state
  }

  connect() {
    // Restore album selection on page load
    this.restoreAlbumSelection();

    // Add hover functionality to existing albums count indicators
    this.initializeExistingIndicators();

    // Listen for various turbo events that might affect images
    document.addEventListener("turbo:before-stream-render", this._handleStreamRender = this.handleStreamRender.bind(this));
    document.addEventListener("turbo:frame-load", this._handleFrameLoad = this.handleFrameLoad.bind(this));
    document.addEventListener("turbo:render", this._handleRender = this.handleRender.bind(this));

    // Listen for context menu add/remove events
    this.element.addEventListener("image-context-menu:added", this._handleContextMenuAdded = this.handleContextMenuAdded.bind(this));
    this.element.addEventListener("image-context-menu:removed", this._handleContextMenuRemoved = this.handleContextMenuRemoved.bind(this));
  }

  disconnect() {
    // Clean up any pending timeouts
    clearTimeout(this.restoreTimeout);

    // Clean up event listeners
    document.removeEventListener("turbo:before-stream-render", this._handleStreamRender);
    document.removeEventListener("turbo:frame-load", this._handleFrameLoad);
    document.removeEventListener("turbo:render", this._handleRender);

    // Clean up context menu event listeners
    this.element.removeEventListener("image-context-menu:added", this._handleContextMenuAdded);
    this.element.removeEventListener("image-context-menu:removed", this._handleContextMenuRemoved);
  }

  initializeExistingIndicators() {
    // Find all existing albums count indicators and add hover functionality
    this.iconsContainerTargets.forEach(container => {
      const imageId = container.getAttribute('data-image-id');
      const albumsIndicator = container.querySelector('.albums-count-indicator');

      if (albumsIndicator && imageId) {
        // Check if hover listener is already added to avoid duplicates
        if (!albumsIndicator.hasAttribute('data-hover-initialized')) {
          this.addAlbumNamesHover(albumsIndicator, imageId);
          albumsIndicator.setAttribute('data-hover-initialized', 'true');
        }
      }
    });
  }

  mark() {
    // Update the count display
    const checkedCount = this.count();
    if (this.hasCountTarget) {
      this.countTarget.textContent = checkedCount;
    }
  }

  hideCheckboxes() {
    this.checkboxes().forEach(e => {
      e.classList.add("invisible")
    })
    this.hideCheckboxesTarget.classList.add("hidden")
    this.showCheckboxesTarget.classList.remove("hidden")
    // hide all the star targets
    this.starTargets.forEach(e => {
      e.classList.add("invisible")
    })
  }

  showCheckboxes() {
    this.checkboxes().forEach(e => {
      e.classList.remove("invisible")
    })
    this.showCheckboxesTarget.classList.add("hidden")
    this.hideCheckboxesTarget.classList.remove("hidden")
    // show all the star targets
    this.starTargets.forEach(e => {
      e.classList.remove("invisible")
    })
  }

  tickall() {
    this.checkboxes().forEach(e => {
      e.checked = true
    })
    this.mark()
  }

  untickall() {
    this.checkboxes().forEach(e => {
      e.checked = false
    })
    this.mark()
  }

  updateViewAlbumLink(event) {
    const albumId = event.target.value;

    // Skip if this is a programmatic update (like updating album counts)
    if (this.isProgrammaticUpdate) {
      return;
    }

    // Save the selection to localStorage
    this.saveAlbumSelection(albumId);

    if (albumId) {
      if (this.viewAlbumTarget) {
        this.viewAlbumTarget.href = `/albums/${albumId}`;
        this.viewAlbumTarget.classList.remove('hidden');

        // Update image states when album changes
        this.showAllImages();
        this.hideImagesInAlbum(albumId);
      }
    } else {
      if (this.viewAlbumTarget) {
        this.viewAlbumTarget.classList.add('hidden');
      }
      this.showAllImages();
    }
  }

  async hideImagesInAlbum(albumId) {
    fetch(`/api/v1/albums/${albumId}/image_ids`)
      .then(response => response.json())
      .then(data => {
        this.hideImages(data);
      })
      .catch(error => {
        console.error('Error fetching album image IDs:', error);
      });
  }

  showAllImages() {
    const allImages = document.querySelectorAll('div[data-image-id][data-response-id]');
    allImages.forEach(image => {
      image.classList.remove('scale-95', 'ring-4', 'ring-sapphire', 'shadow-lg', 'shadow-sapphire/50');
      const checkbox = image.querySelector('input[type="checkbox"]');
      if (checkbox) {
        checkbox.checked = false;
      }
    });
  }

  hideImages(imageIds) {
    const allImages = document.querySelectorAll('div[data-image-id][data-response-id]');
    allImages.forEach(image => {
      const imageId = image.getAttribute('data-image-id');
      if (imageIds.includes(parseInt(imageId))) {
        image.classList.add('scale-95', 'ring-4', 'ring-sapphire', 'shadow-lg', 'shadow-sapphire/50');
        const checkbox = image.querySelector('input[type="checkbox"]');
        if (checkbox) {
          checkbox.checked = true;
        }
      } else {
        image.classList.remove('scale-95', 'ring-4', 'ring-sapphire', 'shadow-lg', 'shadow-sapphire/50');
        const checkbox = image.querySelector('input[type="checkbox"]');
        if (checkbox) {
          checkbox.checked = false;
        }
      }
    });
  }

  snip() {
    const match = window.location.pathname.match(/\/albums\/(\d+)/)
    if (!match) return

    // Collect visible image IDs from the current page
    const imageIds = Array.from(document.querySelectorAll("[data-image-id]"))
      .map(el => el.dataset.imageId)
      .filter(Boolean)

    const params = new URLSearchParams()
    imageIds.forEach(id => params.append("image_ids[]", id))

    window.open(`/albums/${match[1]}/snip?${params.toString()}`, '_blank')
  }

  selectedImageIds() {
    const ids = []
    this.checked().forEach(id => {
      ids.push(id.value)
    })
    return ids
  }

  checked() {
    return this.element.querySelectorAll('input[type="checkbox"]:checked')
  }

  count() {
    return this.checked().length
  }

  checkboxes() {
    return document.querySelectorAll('[name^="add_image_ids"]')
  }

  restoreAlbumSelection() {
    // On /albums/:id/* pages the path unambiguously identifies the album.
    // Use it directly rather than falling back to stale localStorage.
    const pathMatch = window.location.pathname.match(/\/albums\/(\d+)/);
    if (pathMatch) {
      const pathAlbumId = pathMatch[1];
      localStorage.setItem(this.persistKeyValue, pathAlbumId);
      this.selectAlbumById(pathAlbumId);
      return;
    }

    // Check URL params first (takes priority)
    const urlParams = new URLSearchParams(window.location.search);
    const urlAlbumId = urlParams.get('album_id');

    const albumSelect = this.element.querySelector('select[name="album_id"]');
    if (!albumSelect) return;

    if (urlAlbumId) {
      // Save URL album to localStorage and select it
      localStorage.setItem(this.persistKeyValue, urlAlbumId);
      this.selectAlbumById(urlAlbumId);
    } else {
      // Try to restore from localStorage
      const savedAlbumId = localStorage.getItem(this.persistKeyValue);
      if (savedAlbumId) {
        this.selectAlbumById(savedAlbumId);
      }
    }
  }

  selectAlbumById(albumId) {
    const albumSelect = this.element.querySelector('select[name="album_id"]');
    if (albumSelect && albumSelect.querySelector(`option[value="${albumId}"]`)) {
      albumSelect.value = albumId;

      // Update SlimSelect if it exists
      const slimController = this.application.getControllerForElementAndIdentifier(albumSelect, 'slim');
      if (slimController && slimController.select) {
        slimController.select.setSelected(albumId);
      }

      // Trigger the view album link update
      this.updateViewAlbumLink({ target: albumSelect });
    }
  }

  saveAlbumSelection(albumId) {
    if (albumId && albumId !== '') {
      localStorage.setItem(this.persistKeyValue, albumId);
    }
  }

  // Real-time album management
  handleImageCheckbox(event) {
    const checkbox = event.target;
    const imageId = checkbox.value;

    // Determine the album ID based on the page context
    let selectedAlbumId = null;

    // First, try to get from album select dropdown (images index page)
    const albumSelect = this.element.querySelector('select[name="album_id"]');
    if (albumSelect && albumSelect.value) {
      selectedAlbumId = albumSelect.value;
    } else {
      // If no select dropdown, check if we're on an album show page
      const currentPath = window.location.pathname;
      const albumShowMatch = currentPath.match(/\/albums\/(\d+)/);
      if (albumShowMatch) {
        selectedAlbumId = albumShowMatch[1];
      }
    }

    if (!selectedAlbumId || selectedAlbumId === '') {
      // If no album context, just update the UI count
      this.mark();
      return;
    }

    if (checkbox.checked) {
      // Add image to album
      this.addImageToAlbum(imageId, selectedAlbumId);
    } else {
      // Remove image from album
      this.removeImageFromAlbum(imageId, selectedAlbumId);
    }

    // Update the count display
    this.mark();
  }

  addImageToAlbum(imageId, albumId) {
    const imageElement = document.querySelector(`div[data-image-id="${imageId}"][data-response-id]`);
    const checkbox = document.querySelector(`input[value="${imageId}"]`);

    // Add loading state
    this.setCheckboxLoading(checkbox, true);

    fetch('/albums_images', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken(),
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        albums_image: {
          image_id: imageId,
          album_id: albumId
        }
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          // Check if we're on an album show page vs images index page
          const currentPath = window.location.pathname;
          const isAlbumShowPage = currentPath.match(/\/albums\/(\d+)$/);

          if (!isAlbumShowPage) {
            // On images index page: apply selection styling to show it's in the album
            if (imageElement) {
              imageElement.classList.add('scale-95', 'ring-4', 'ring-sapphire', 'shadow-lg', 'shadow-sapphire/50');
            }

            // Update the albums count indicator on the image card
            this.updateImageAlbumsCount(imageId, 1);

            // Handle album renaming (draft album becoming a real album)
            if (data.album && data.album.was_created) {
              // The draft album was renamed, update the dropdown
              this.handleAlbumRenamed(data.album);
            } else {
              // Update the album count for existing album
              this.updateAlbumCount(albumId, 1);
            }
          }
          // On album show page: don't update UI immediately

          // Remove loading state
          this.setCheckboxLoading(checkbox, false);
        } else {
          // If failed, uncheck the checkbox and remove loading state
          this.setCheckboxLoading(checkbox, false);
          if (checkbox) checkbox.checked = false;
          console.error('Failed to add image to album:', data.message);
        }
      })
      .catch(error => {
        // Remove loading state and uncheck the checkbox
        this.setCheckboxLoading(checkbox, false);
        if (checkbox) checkbox.checked = false;
        console.error('Error adding image to album:', error);
      });
  }

  removeImageFromAlbum(imageId, albumId) {
    const imageElement = document.querySelector(`div[data-image-id="${imageId}"][data-response-id]`);
    const checkbox = document.querySelector(`input[value="${imageId}"]`);

    // Add loading state
    this.setCheckboxLoading(checkbox, true);

    fetch(`/albums_images/remove`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken(),
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        image_ids: imageId,
        album_id: albumId
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          // Check if we're on an album show page vs images index page
          const currentPath = window.location.pathname;
          const isAlbumShowPage = currentPath.match(/\/albums\/(\d+)$/);

          if (!isAlbumShowPage) {
            // On images index page: remove selection styling to show it's no longer in the album
            if (imageElement) {
              imageElement.classList.remove('scale-95', 'ring-4', 'ring-sapphire', 'shadow-lg', 'shadow-sapphire/50');
            }

            // Update the albums count indicator on the image card
            this.updateImageAlbumsCount(imageId, -1);

            // Update the album count in the dropdown
            this.updateAlbumCount(albumId, -1);
          }
          // On album show page: don't update UI immediately - user can see effect after page refresh

          // Remove loading state
          this.setCheckboxLoading(checkbox, false);
        } else {
          // If failed, re-check the checkbox and remove loading state
          this.setCheckboxLoading(checkbox, false);
          if (checkbox) checkbox.checked = true;
          console.error('Failed to remove image from album:', data.message);
        }
      })
      .catch(error => {
        // Remove loading state and re-check the checkbox
        this.setCheckboxLoading(checkbox, false);
        if (checkbox) checkbox.checked = true;
        console.error('Error removing image from album:', error);
      });
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]');
    return token ? token.getAttribute('content') : '';
  }

  updateAlbumCount(albumId, change) {
    const albumSelect = this.element.querySelector('select[name="album_id"]');
    if (!albumSelect) return;

    const option = albumSelect.querySelector(`option[value="${albumId}"]`);
    if (!option) return;

    // Extract current count from option text (format: "Album Name [status] (123)")
    const currentText = option.textContent;
    const countMatch = currentText.match(/\((\d+)\)$/);

    if (countMatch) {
      const currentCount = parseInt(countMatch[1]);
      const newCount = Math.max(0, currentCount + change);
      const newText = currentText.replace(/\(\d+\)$/, `(${newCount})`);
      option.textContent = newText;

      // Update SlimSelect if it exists - but prevent change events during update
      const slimController = this.application.getControllerForElementAndIdentifier(albumSelect, 'slim');
      if (slimController && slimController.select) {
        // Temporarily set a flag to indicate we're programmatically updating
        this.isProgrammaticUpdate = true;

        // Refresh the SlimSelect to show updated text
        slimController.select.setData(Array.from(albumSelect.options).map(opt => ({
          text: opt.textContent,
          value: opt.value,
          selected: opt.selected
        })));

        // Clear the flag after a brief delay
        setTimeout(() => {
          this.isProgrammaticUpdate = false;
        }, 10);
      }
    }
  }

  setCheckboxLoading(checkbox, isLoading) {
    if (!checkbox) return;

    if (isLoading) {
      // Disable the checkbox and hide it temporarily
      checkbox.disabled = true;
      checkbox.style.opacity = '0.3';

      // Create and add spinner if it doesn't exist
      let spinner = checkbox.parentElement.querySelector('.checkbox-spinner');
      if (!spinner) {
        spinner = document.createElement('div');
        spinner.className = 'checkbox-spinner absolute flex items-center justify-center';
        // Position the spinner exactly over the checkbox
        spinner.style.cssText = `
          width: 24px;
          height: 24px;
          top: calc(50% - 7px);
          left: 7px;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.9);
          border-radius: 50%;
          z-index: 10;
        `;
        spinner.innerHTML = `
          <svg class="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        `;

        // Make the container relative if it isn't already
        const checkboxContainer = checkbox.parentElement;
        if (window.getComputedStyle(checkboxContainer).position === 'static') {
          checkboxContainer.style.position = 'relative';
        }

        checkboxContainer.appendChild(spinner);
      }
      spinner.style.display = 'flex';
    } else {
      // Re-enable the checkbox
      checkbox.disabled = false;
      checkbox.style.opacity = '';

      // Hide spinner
      const spinner = checkbox.parentElement.querySelector('.checkbox-spinner');
      if (spinner) {
        spinner.style.display = 'none';
      }
    }
  }

  updateImageAlbumsCount(imageId, change) {
    // Find the icons container using Stimulus target
    const iconsContainer = this.iconsContainerTargets.find(target =>
      target.getAttribute('data-image-id') === imageId.toString()
    );

    if (!iconsContainer) {
      console.warn(`Could not find icons container for image ${imageId}`);
      return;
    }

    // Find the albums count indicator in the container
    let albumsIndicator = iconsContainer.querySelector('.albums-count-indicator');

    if (!albumsIndicator && change > 0) {
      // Create the indicator if it doesn't exist and we're adding
      albumsIndicator = document.createElement('div');
      albumsIndicator.className = 'albums-count-indicator bg-emerald-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold';
      albumsIndicator.innerHTML = '1';
      albumsIndicator.title = 'In 1 album';

      // Add hover listeners to show album names
      this.addAlbumNamesHover(albumsIndicator, imageId);

      // Insert before the rating indicator or avatar
      const firstChild = iconsContainer.firstElementChild;
      iconsContainer.insertBefore(albumsIndicator, firstChild);
    } else if (albumsIndicator) {
      // Update existing indicator
      const currentCountMatch = albumsIndicator.textContent.match(/(\d+)/);
      const currentCount = currentCountMatch ? parseInt(currentCountMatch[1]) : 0;
      const newCount = Math.max(0, currentCount + change);

      if (newCount === 0) {
        // Remove the indicator if count reaches 0
        albumsIndicator.remove();
      } else {
        // Update the indicator with new count
        albumsIndicator.innerHTML = `${newCount}`;
        albumsIndicator.title = `In ${newCount} album${newCount === 1 ? '' : 's'}`;
      }
    }
  }

  addAlbumNamesHover(albumsIndicator, imageId) {
    // Cache for album names to avoid repeated API calls
    let albumNamesCache = null;
    let isLoading = false;

    const handleMouseEnter = () => {
      if (albumNamesCache) {
        albumsIndicator.title = `Albums: ${albumNamesCache.join(', ')}`;
      } else if (!isLoading) {
        isLoading = true;
        albumsIndicator.title = 'Loading album names...';

        fetch(`/api/v1/albums/for_image/${imageId}`)
          .then(response => response.json())
          .then(data => {
            albumNamesCache = data.album_names;
            const newTitle = albumNamesCache.length > 0
              ? `Albums: ${albumNamesCache.join(', ')}`
              : 'No albums found';

            // Force tooltip update by triggering a mouseleave/mouseenter cycle
            albumsIndicator.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
            setTimeout(() => {
              albumsIndicator.title = newTitle;
              albumsIndicator.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
            }, 50);
          })
          .catch(error => {
            console.error('Error fetching album names:', error);
            albumsIndicator.title = 'Error loading album names';
          })
          .finally(() => {
            isLoading = false;
          });
      }
    };

    const handleMouseLeave = () => {
      // Reset to basic title when mouse leaves
      const count = albumsIndicator.textContent;
      albumsIndicator.title = `In ${count} album${count === '1' ? '' : 's'}`;
    };

    albumsIndicator.addEventListener('mouseenter', handleMouseEnter);
    albumsIndicator.addEventListener('mouseleave', handleMouseLeave);
  }

  handleAlbumRenamed(albumData) {
    const albumSelect = this.element.querySelector('select[name="album_id"]');
    if (!albumSelect) return;

    // Find the option with the album ID and update its text
    const option = albumSelect.querySelector(`option[value="${albumData.id}"]`);
    if (option) {
      option.textContent = albumData.name;
    }

    // Update SlimSelect if it exists
    const slimController = this.application.getControllerForElementAndIdentifier(albumSelect, 'slim');
    if (slimController && slimController.select) {
      // Temporarily set a flag to indicate we're programmatically updating
      this.isProgrammaticUpdate = true;

      // Refresh the SlimSelect with updated text
      slimController.select.setData(Array.from(albumSelect.options).map(opt => ({
        text: opt.textContent,
        value: opt.value,
        selected: opt.selected
      })));

      // Clear the flag after a brief delay
      setTimeout(() => {
        this.isProgrammaticUpdate = false;
      }, 10);
    }
  }

  handleRender(event) {
    // This event fires after turbo renders content (including turbo streams)
    console.log('Turbo render event detected, checking for image states to restore');

    // Initialize hover functionality for any new indicators
    this.initializeExistingIndicators();

    // Check if there are any images on the page that need state restoration
    const imagesWithCheckboxes = document.querySelectorAll('div[data-image-id][data-response-id] input[type="checkbox"]');
    if (imagesWithCheckboxes.length > 0) {
      const albumSelect = this.element.querySelector('select[name="album_id"]');
      const selectedAlbumId = albumSelect ? albumSelect.value : null;

      if (selectedAlbumId) {
        // Restore the greyed out state for images in the selected album
        this.showAllImages();
        this.hideImagesInAlbum(selectedAlbumId);
        console.log('Image states restored after render for album:', selectedAlbumId);
      }
    }
  }

  handleStreamRender(event) {
    // Check if this stream render affects the images content
    const streamElement = event.target;
    const action = streamElement.getAttribute('action');
    const target = streamElement.getAttribute('target');

    // Handle both main images frame and mission images frames
    if (target === 'images' || (target && target.endsWith('_images_table'))) {
      console.log('Turbo stream render detected for images frame, will restore image states');

      // Use a slight delay to ensure the DOM has been updated
      setTimeout(() => {
        // Initialize hover functionality for any new indicators after pagination
        this.initializeExistingIndicators();

        const albumSelect = this.element.querySelector('select[name="album_id"]');
        const selectedAlbumId = albumSelect ? albumSelect.value : null;

        if (selectedAlbumId) {
          // Restore the greyed out state for images in the selected album
          this.showAllImages();
          this.hideImagesInAlbum(selectedAlbumId);
          console.log('Image states restored after turbo stream render for album:', selectedAlbumId);
        }
      }, 50);
    }
  }

  handleFrameLoad(event) {
    // Handle frame loads for both main images frame and mission images frames
    if (event.target.id === 'images' || (event.target.id && event.target.id.endsWith('_images_table'))) {
      console.log('Images frame loaded, restoring image states');

      // Initialize hover functionality for any new indicators after frame load
      this.initializeExistingIndicators();

      // Get the currently selected album
      const albumSelect = this.element.querySelector('select[name="album_id"]');
      const selectedAlbumId = albumSelect ? albumSelect.value : null;

      if (selectedAlbumId) {
        // Restore the greyed out state for images in the selected album
        this.showAllImages();
        this.hideImagesInAlbum(selectedAlbumId);
        console.log('Image states restored after frame load for album:', selectedAlbumId);
      }
    }
  }

  removeSelected(event) {
    event.preventDefault();

    // Get the album ID from the current URL (we're on an album show page)
    const currentPath = window.location.pathname;
    const albumShowMatch = currentPath.match(/\/albums\/(\d+)/);
    if (!albumShowMatch) {
      alert('Remove is only available on album pages.');
      return;
    }

    const albumId = albumShowMatch[1];

    // Get all checked image IDs
    const checkedBoxes = this.checked();
    if (checkedBoxes.length === 0) {
      alert('Please select at least one image to remove.');
      return;
    }

    const imageCount = checkedBoxes.length;
    if (!confirm(`Remove ${imageCount} image${imageCount === 1 ? '' : 's'} from this album?`)) {
      return;
    }

    const imageIds = Array.from(checkedBoxes).map(checkbox => checkbox.value).join(',');

    // Add loading state to the button
    const removeButton = event.target;
    const originalText = removeButton.textContent;
    removeButton.disabled = true;
    removeButton.textContent = 'Removing...';

    fetch('/albums_images/remove', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.getCSRFToken(),
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        image_ids: imageIds,
        album_id: albumId
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          // Reload the images turbo frame to reflect the removal
          const imagesFrame = document.querySelector('turbo-frame[id$="_images"]');
          if (imagesFrame && imagesFrame.src) {
            imagesFrame.reload();
          } else {
            // Fallback: reload the page
            window.location.reload();
          }
        } else {
          alert('Failed to remove images from album.');
          removeButton.disabled = false;
          removeButton.textContent = originalText;
        }
      })
      .catch(error => {
        console.error('Error removing images from album:', error);
        alert('Failed to remove images from album.');
        removeButton.disabled = false;
        removeButton.textContent = originalText;
      });
  }

  addToAlbum(event) {
    event.preventDefault();

    // Get the selected album ID
    const albumSelect = this.element.querySelector('select[name="album_id"]');
    if (!albumSelect || !albumSelect.value) {
      alert('Please select an album first.');
      return;
    }

    const albumId = albumSelect.value;

    // Get all checked image IDs
    const checkedBoxes = this.checked();
    if (checkedBoxes.length === 0) {
      alert('Please select at least one image to add.');
      return;
    }

    // Extract image IDs from checked checkboxes
    const imageIds = Array.from(checkedBoxes).map(checkbox => checkbox.value);

    // Add loading state to the button
    const addButton = event.target;
    const originalText = addButton.textContent;
    addButton.disabled = true;
    addButton.textContent = 'Adding...';

    // Process each image
    let completed = 0;
    let errors = 0;

    const processComplete = () => {
      // Reset button state
      addButton.disabled = false;
      addButton.textContent = originalText;

      // Show result message
      if (errors === 0) {
        // Success - uncheck all boxes and update count
        this.untickall();
        alert(`Successfully added ${imageIds.length} image(s) to the album.`);
      } else {
        alert(`Added ${completed - errors} image(s) successfully. ${errors} are already in the album.`);
      }
    };

    // Add each image to the album
    imageIds.forEach(imageId => {
      fetch('/albums_images', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          albums_image: {
            image_id: imageId,
            album_id: albumId
          }
        })
      })
        .then(response => response.json())
        .then(data => {
          completed++;

          if (data.success) {
            // Update UI for successful addition
            const imageElement = document.querySelector(`div[data-image-id="${imageId}"][data-response-id]`);
            if (imageElement) {
              imageElement.classList.add('scale-95', 'ring-4', 'ring-sapphire', 'shadow-lg', 'shadow-sapphire/50');
            }

            // Update the albums count indicator on the image card
            this.updateImageAlbumsCount(imageId, 1);

            // Handle album renaming if it was a draft album
            if (data.album && data.album.was_created) {
              this.handleAlbumRenamed(data.album);
            } else {
              this.updateAlbumCount(albumId, 1);
            }
          } else {
            errors++;
            console.error('Failed to add image to album:', data.message);
          }

          // Check if all requests are complete
          if (completed === imageIds.length) {
            processComplete();
          }
        })
        .catch(error => {
          completed++;
          errors++;
          console.error('Error adding image to album:', error);

          // Check if all requests are complete
          if (completed === imageIds.length) {
            processComplete();
          }
        });
    });
  }

  handleContextMenuAdded(event) {
    const { imageId, albumId } = event.detail;
    if (imageId) {
      this.updateImageAlbumsCount(imageId, 1);

      // Update album count in dropdown if visible
      if (albumId) {
        this.updateAlbumCount(albumId, 1);
      }

      // Apply selection styling on images index page
      const imageElement = document.querySelector(`div[data-image-id="${imageId}"][data-response-id]`);
      const albumSelect = this.element.querySelector('select[name="album_id"]');
      if (imageElement && albumSelect && albumSelect.value === String(albumId)) {
        imageElement.classList.add('scale-95', 'ring-4', 'ring-sapphire', 'shadow-lg', 'shadow-sapphire/50');
        const checkbox = imageElement.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = true;
      }
    }
  }

  handleContextMenuRemoved(event) {
    const { imageId, albumId } = event.detail;
    if (imageId) {
      this.updateImageAlbumsCount(imageId, -1);

      if (albumId) {
        this.updateAlbumCount(albumId, -1);
      }

      const imageElement = document.querySelector(`div[data-image-id="${imageId}"][data-response-id]`);
      if (imageElement) {
        imageElement.classList.remove('scale-95', 'ring-4', 'ring-sapphire', 'shadow-lg', 'shadow-sapphire/50');
        const checkbox = imageElement.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = false;
      }
    }
  }
}
